import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, FileText, LoaderCircle, Mic, Square } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { ModelQuickDropdown } from '../../features/models/ModelQuickDropdown';
import {
  CHAT_TOOL_KEYS,
  ChatComposerPlusMenu,
  ChatToolChip
} from '../../features/chat/ChatComposerPlusMenu';
import { MessageMarkdown } from '../../features/chat/MessageMarkdown';
import {
  AssistantMessageActions,
  UserMessageActions,
  copyTextToClipboard
} from '../../features/chat/MessageActions';
import { AskRismanSelection } from '../../features/chat/AskRismanSelection';
import { QuoteBar, QuotedMessagePreview } from '../../features/chat/QuoteBar';
import { TemporaryChatButton } from '../../features/chat/TemporaryChatButton';
import { ShareConversationButton } from '../../features/chat/ShareConversationButton';
import { ShareConversationDialog } from '../../features/chat/ShareConversationDialog';
import { VoiceDictationBar } from '../../features/chat/VoiceDictationBar';
import { ChatFreeQuotaBanner } from '../../features/chat/ChatFreeQuotaBanner';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { speakText, stopSpeaking, useSpeechInput } from '../../hooks/useSpeechInput';
import {
  createConversationApi,
  editConversationMessageApi,
  fetchConversationApi,
  fetchConversationMessagesApi,
  regenerateConversationMessageApi,
  setConversationMessageFeedbackApi,
  streamConversationMessageApi
} from '../../api/conversations.api';
import { fetchFolderApi } from '../../api/folders.api';
import { uploadMediaFilesApi } from '../../api/media.api';
import {
  ACCEPTED_CHAT_FILE_ACCEPT,
  isAcceptedChatFile,
  isImageFile,
  toStoredAttachmentUrl
} from '../../constants/acceptedChatFiles';
import { ChatMessageAttachments } from '../../features/chat/ChatMessageAttachments';
import {
  loadStoredModel,
  modelSupportsImage,
  getModelByKey,
  resolveImageModelForReferences,
  storeSelectedModel
} from '../../constants/chatImageModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import { DEFAULT_LLM_PROVIDER_KEY } from '../../constants/llmProviders';
import { fetchMyPackageApi } from '../../api/packages.api';
import { getApiErrorMessage } from '../../api/apiClient';
import {
  notifyRecentActivityUpsert,
  subscribeRecentActivity
} from '../../features/activity/recentActivityEvents';

const MAX_ATTACHMENTS = 5;

const resolveToolFlags = (activeTool) => {
  switch (activeTool) {
    case CHAT_TOOL_KEYS.IMAGE:
      return { generateImage: true, enableWebSearch: false, mode: null };
    case CHAT_TOOL_KEYS.THINK:
      return { generateImage: false, enableWebSearch: false, mode: 'think' };
    case CHAT_TOOL_KEYS.SEARCH:
      return { generateImage: false, enableWebSearch: true, mode: 'search' };
    case CHAT_TOOL_KEYS.CREATE_FILE:
      return { generateImage: false, enableWebSearch: false, mode: 'create_file' };
    case CHAT_TOOL_KEYS.RESEARCH:
      return { generateImage: false, enableWebSearch: true, mode: 'research' };
    case CHAT_TOOL_KEYS.STUDY:
      return { generateImage: false, enableWebSearch: false, mode: 'study' };
    default:
      return { generateImage: false, enableWebSearch: false, mode: null };
  }
};

export const ChatPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, language } = useI18n();
  const { models: catalogModels } = useLlmCatalog();
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [quote, setQuote] = useState(null);
  const [model, setModel] = useState(() => loadStoredModel('chat'));
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [entitlement, setEntitlement] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isTemporary, setIsTemporary] = useState(() => searchParams.get('temporary') === '1');
  const [folderId, setFolderId] = useState(() => searchParams.get('folderId') || null);
  const [folderName, setFolderName] = useState('');
  const [conversationTitle, setConversationTitle] = useState('');
  const [conversationCreatedAt, setConversationCreatedAt] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const prevConversationIdRef = useRef(conversationId);
  const prevLoadingRef = useRef(loading);
  const abortRef = useRef(null);
  const pendingConversationIdRef = useRef(null);
  const skipLoadConversationIdRef = useRef(null);
  const promptRef = useRef('');
  const fileInputRef = useRef(null);

  const activeModel = useMemo(() => getModelByKey('chat', model, catalogModels), [catalogModels, model]);
  const returnPath = conversationId ? `/chat/${conversationId}` : '/chat';
  const freeQuotaExhausted =
    entitlement?.hasPackage === false &&
    entitlement.freeTierTokensRemaining != null &&
    Number(entitlement.freeTierTokensRemaining) <= 0;

  const reloadEntitlement = useCallback(() => {
    fetchMyPackageApi()
      .then(setEntitlement)
      .catch(() => {});
  }, []);

  useEffect(() => {
    promptRef.current = prompt;
  }, [prompt]);

  useEffect(() => {
    if (!conversationId) {
      setIsTemporary(searchParams.get('temporary') === '1');
      setFolderId(searchParams.get('folderId') || null);
    }
  }, [conversationId, searchParams]);

  useEffect(() => {
    let cancelled = false;
    const loadFolderMeta = async () => {
      if (!folderId) {
        setFolderName('');
        return;
      }
      try {
        const folder = await fetchFolderApi(folderId);
        if (!cancelled) setFolderName(folder.name || '');
      } catch {
        if (!cancelled) setFolderName('');
      }
    };
    loadFolderMeta();
    return () => {
      cancelled = true;
    };
  }, [folderId]);

  useEffect(() => {
    const stored = loadStoredModel('chat', catalogModels);
    setModel(stored);
  }, [catalogModels, location.key]);

  useEffect(() => {
    let cancelled = false;
    fetchMyPackageApi()
      .then((current) => {
        if (!cancelled) {
          setEntitlement(current);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntitlement(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const conversationChanged = prevConversationIdRef.current !== conversationId;
    const historyJustLoaded = prevLoadingRef.current && !loading;
    const messageCount = messages.length;
    const countIncreased = messageCount > prevMessageCountRef.current;

    prevConversationIdRef.current = conversationId;
    prevLoadingRef.current = loading;
    prevMessageCountRef.current = messageCount;

    if (loading || (!countIncreased && !conversationChanged && !historyJustLoaded)) {
      return;
    }

    bottomRef.current?.scrollIntoView({
      behavior: conversationChanged || historyJustLoaded ? 'auto' : 'smooth'
    });
  }, [conversationId, loading, messages.length]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!conversationId) {
        if (!pendingConversationIdRef.current) {
          setMessages([]);
          setConversationTitle('');
          setConversationCreatedAt(null);
          setError('');
        }
        setLoading(false);
        return;
      }

      if (skipLoadConversationIdRef.current === conversationId) {
        skipLoadConversationIdRef.current = null;
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const [items, conversation] = await Promise.all([
          fetchConversationMessagesApi(conversationId),
          fetchConversationApi(conversationId)
        ]);
        if (!cancelled) {
          setMessages(items);
          setConversationTitle(conversation.title || '');
          setConversationCreatedAt(conversation.createdAt || null);
          setFolderId(conversation.folderId || null);
          setIsTemporary(Boolean(conversation.isTemporary));
        }
      } catch (err) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, t('chatLoadError')));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [conversationId, t]);

  useEffect(() => {
    const openNewConversation = () => {
      abortRef.current?.abort();
      pendingConversationIdRef.current = null;
      skipLoadConversationIdRef.current = null;
      setMessages([]);
      setConversationTitle('');
      setConversationCreatedAt(null);
      setQuote(null);
      setError('');
      setEditingId(null);
      setEditingText('');
      setAttachments((prev) => {
        prev.forEach((item) => {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        return [];
      });
      setSending(false);
      setLoading(false);
      navigate('/chat', { replace: true });
    };

    return subscribeRecentActivity({
      onRemove: (item) => {
        if (!conversationId) return;
        if (item?.all && item.type === 'conversation') {
          openNewConversation();
          return;
        }
        if (item?.type === 'conversation' && item.id === conversationId) {
          openNewConversation();
        }
      }
    });
  }, [conversationId, navigate]);

  useEffect(
    () => () => {
      stopSpeaking();
    },
    []
  );

  const ensureConversation = useCallback(async () => {
    if (conversationId) {
      return conversationId;
    }
    if (pendingConversationIdRef.current) {
      return pendingConversationIdRef.current;
    }

    const created = await createConversationApi({
      model,
      provider: activeModel.provider || DEFAULT_LLM_PROVIDER_KEY,
      isTemporary,
      folderId: folderId || null
    });
    pendingConversationIdRef.current = created.id;
    if (created.title) setConversationTitle(created.title);
    if (created.createdAt) setConversationCreatedAt(created.createdAt);
    if (created.folderId) setFolderId(created.folderId);
    notifyRecentActivityUpsert({
      id: created.id,
      type: 'conversation',
      title: created.title || '',
      updatedAt: created.updatedAt || new Date().toISOString(),
      isPinned: Boolean(created.isPinned),
      isArchived: Boolean(created.isArchived),
      folderId: created.folderId || folderId || null,
      isTemporary: Boolean(created.isTemporary ?? isTemporary)
    });
    return created.id;
  }, [activeModel.provider, conversationId, folderId, isTemporary, model]);

  const handleAddFiles = async (files) => {
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) return;

    const selected = files.slice(0, remaining);
    const accepted = selected.filter(isAcceptedChatFile);
    if (!accepted.length) {
      setError(t('folderFilesUnsupportedType'));
      return;
    }

    const drafts = accepted.map((file) => {
      const kind = isImageFile(file) ? 'image' : 'file';
      return {
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        file,
        name: file.name,
        contentType: file.type,
        kind,
        previewUrl: kind === 'image' ? URL.createObjectURL(file) : '',
        url: ''
      };
    });
    setAttachments((prev) => [...prev, ...drafts]);
    setUploading(true);
    setError(accepted.length < selected.length ? t('folderFilesUnsupportedType') : '');

    try {
      const { urls, errors } = await uploadMediaFilesApi(accepted);
      setAttachments((prev) => {
        const draftIds = new Set(drafts.map((draft) => draft.id));
        let urlIndex = 0;
        return prev
          .map((item) => {
            if (!draftIds.has(item.id)) return item;
            const url = urls[urlIndex];
            urlIndex += 1;
            if (!url) return null;
            return {
              ...item,
              url,
              previewUrl: item.kind === 'image' ? url : item.previewUrl
            };
          })
          .filter(Boolean);
      });
      if (errors.length) setError(t('attachmentUploadPartialError'));
    } catch (err) {
      setError(getApiErrorMessage(err, t('attachmentUploadFailed')));
      setAttachments((prev) => prev.filter((item) => !drafts.some((draft) => draft.id === item.id)));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (item) => {
    if (item.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewUrl);
    }
    setAttachments((prev) => prev.filter((entry) => entry.id !== item.id));
  };

  const settleStoppedAssistant = (tempAssistantId) => {
    setMessages((prev) => {
      const assistant = prev.find((item) => item.id === tempAssistantId);
      const hasPartial = Boolean(
        String(assistant?.content || assistant?.message || '').trim() || assistant?.generatedImageUrls?.length
      );
      if (!assistant) {
        return prev;
      }
      if (hasPartial) {
        return prev.map((item) => (item.id === tempAssistantId ? { ...item, streaming: false } : item));
      }
      return prev.filter((item) => item.id !== tempAssistantId);
    });
  };

  const stopGeneration = useCallback(() => {
    stopSpeaking();
    abortRef.current?.abort();
  }, []);

  const applyStreamHandlers = (tempAssistantId, generated) => ({
    onDelta: (_chunk, fullText) => {
      setMessages((prev) =>
        prev.map((item) =>
          item.id === tempAssistantId ? { ...item, message: fullText, content: fullText } : item
        )
      );
    },
    onImage: (url) => {
      if (!url) return;
      generated.push(url);
      setMessages((prev) =>
        prev.map((item) =>
          item.id === tempAssistantId ? { ...item, generatedImageUrls: [...generated] } : item
        )
      );
    },
    onTitle: (title) => {
      if (!title) return;
      setConversationTitle(title);
      const id =
        conversationId || pendingConversationIdRef.current || skipLoadConversationIdRef.current;
      if (!id) return;
      notifyRecentActivityUpsert({
        id,
        type: 'conversation',
        title,
        updatedAt: new Date().toISOString()
      });
    },
    onResult: (result, finalAssistantText) => {
      if (!result) return;
      const messageText = result.message || result.Message || finalAssistantText;
      setMessages((prev) =>
        prev.map((item) =>
          item.id === tempAssistantId
            ? {
                ...item,
                id: result.id || result.Id || item.id,
                message: messageText,
                content: messageText,
                generatedImageUrls:
                  result.generatedImageUrls || result.GeneratedImageUrls || item.generatedImageUrls,
                feedback: result.feedback || result.Feedback || null,
                streaming: false
              }
            : item
        )
      );
    }
  });

  const sendMessage = useCallback(
    async ({ text: rawText, fromVoice = false } = {}) => {
      const text = (rawText ?? promptRef.current).trim();
      const imageUrls = attachments.map((item) => toStoredAttachmentUrl(item)).filter(Boolean);
      const hasImages = attachments.some((item) => item.kind === 'image' && item.url);
      if ((!text && imageUrls.length === 0) || sending || uploading) return;

      const flags = resolveToolFlags(activeTool);
      const generateImage = flags.generateImage || modelSupportsImage(model, catalogModels);
      const resolvedModel = generateImage
        ? resolveImageModelForReferences(model, hasImages, catalogModels)
        : model;
      const catalogModel = getModelByKey('chat', resolvedModel, catalogModels);
      if (catalogModel?.allowed === false) {
        setError(catalogModel.lockedReason || t('noPackageBanner'));
        return;
      }
      if (
        entitlement?.hasPackage === false &&
        entitlement.freeTierTokensRemaining != null &&
        Number(entitlement.freeTierTokensRemaining) <= 0
      ) {
        return;
      }

      setSending(true);
      setError('');
      setPrompt('');
      setAttachments([]);
      storeSelectedModel('chat', model);
      const quotedText = quote?.text || null;
      const quotedMessageId = quote?.messageId || null;
      setQuote(null);

      const tempUserId = `temp-user-${Date.now()}`;
      const tempAssistantId = `temp-assistant-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: tempUserId,
          role: 'user',
          message: text,
          content: text,
          imageUrls,
          generatedImageUrls: [],
          quotedText,
          quotedMessageId,
          createdAt: new Date().toISOString()
        },
        {
          id: tempAssistantId,
          role: 'assistant',
          message: '',
          content: '',
          imageUrls: [],
          generatedImageUrls: [],
          createdAt: new Date().toISOString(),
          streaming: true
        }
      ]);

      const controller = new AbortController();
      abortRef.current = controller;
      const wasNewConversation = !conversationId;
      let finalAssistantText = '';
      let createdId = null;
      let failed = false;
      const generated = [];

      try {
        createdId = await ensureConversation();

        if (wasNewConversation && createdId) {
          const provisionalTitle = text.slice(0, 80).trim() || t('chatEmptyTitle');
          notifyRecentActivityUpsert({
            id: createdId,
            type: 'conversation',
            title: provisionalTitle,
            updatedAt: new Date().toISOString(),
            isTemporary,
            folderId: folderId || undefined
          });
          setConversationTitle((prev) => prev || provisionalTitle);
        }

        await streamConversationMessageApi({
          conversationId: createdId,
          prompt: text || t('chatAttachmentOnlyPrompt'),
          provider: activeModel.provider || DEFAULT_LLM_PROVIDER_KEY,
          model: resolvedModel,
          generateImage,
          enableWebSearch: flags.enableWebSearch,
          mode: flags.mode,
          imageUrls,
          quotedText,
          quotedMessageId,
          signal: controller.signal,
          ...applyStreamHandlers(tempAssistantId, generated),
          onDelta: (_chunk, fullText) => {
            finalAssistantText = fullText;
            applyStreamHandlers(tempAssistantId, generated).onDelta(_chunk, fullText);
          },
          onResult: (result) => {
            const messageText = result?.message || result?.Message || finalAssistantText;
            finalAssistantText = messageText;
            applyStreamHandlers(tempAssistantId, generated).onResult(result, finalAssistantText);
          }
        });

        if (fromVoice && finalAssistantText && !controller.signal.aborted) {
          speakText(finalAssistantText, {
            language: language === 'fa' ? 'fa-IR' : 'en-US'
          });
        }
      } catch (err) {
        const aborted = err?.name === 'AbortError' || controller.signal.aborted;
        if (aborted) {
          settleStoppedAssistant(tempAssistantId);
        } else {
          failed = true;
          setError(getApiErrorMessage(err, t('chatSendError')));
          setMessages((prev) => prev.filter((item) => item.id !== tempAssistantId && item.id !== tempUserId));
          if (wasNewConversation) pendingConversationIdRef.current = null;
        }
      } finally {
        setSending(false);
        abortRef.current = null;
        setMessages((prev) => prev.map((item) => (item.streaming ? { ...item, streaming: false } : item)));
        if (wasNewConversation && createdId && !failed) {
          skipLoadConversationIdRef.current = createdId;
          pendingConversationIdRef.current = null;
          navigate(`/chat/${createdId}${isTemporary ? '?temporary=1' : ''}`, { replace: true });
        }
        reloadEntitlement();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeModel.provider,
      activeTool,
      catalogModels,
      attachments,
      conversationId,
      ensureConversation,
      entitlement,
      isTemporary,
      model,
      navigate,
      quote,
      reloadEntitlement,
      sending,
      t,
      uploading
    ]
  );

  const bootstrappedPromptRef = useRef(false);

  useEffect(() => {
    if (bootstrappedPromptRef.current || conversationId) return;
    const initial = String(location.state?.initialPrompt || '').trim();
    if (!initial) return;
    bootstrappedPromptRef.current = true;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    void sendMessage({ text: initial });
  }, [conversationId, location.pathname, location.search, location.state, navigate, sendMessage]);

  const speechInput = useSpeechInput({
    language: language === 'fa' ? 'fa-IR' : 'en-US',
    onResult: (transcript) => {
      if (!transcript) return;
      setPrompt(transcript);
    },
    onError: (code) => {
      if (code === 'unsupported') {
        setError(t('chatVoiceUnsupported'));
        return;
      }
      if (code !== 'aborted' && code !== 'no-speech') {
        setError(t('chatVoiceError'));
      }
    }
  });

  const handleSend = async (event) => {
    event?.preventDefault?.();
    if (sending) return;
    speechInput.stop();
    await sendMessage({ text: prompt });
  };

  const toggleVoiceInput = () => {
    setError('');
    if (!speechInput.supported) {
      setError(t('chatVoiceUnsupported'));
      return;
    }
    speechInput.toggle();
  };

  const handleFeedback = async (message, next) => {
    const feedback = message.feedback === next ? null : next;
    setMessages((prev) =>
      prev.map((item) => (item.id === message.id ? { ...item, feedback } : item))
    );
    try {
      await setConversationMessageFeedbackApi(message.id, feedback);
    } catch (err) {
      setError(getApiErrorMessage(err, t('chatSendError')));
    }
  };

  const handleRegenerate = async (message) => {
    if (sending) return;
    setSending(true);
    setError('');
    const tempAssistantId = `temp-assistant-${Date.now()}`;
    setMessages((prev) =>
      prev
        .filter((item) => item.id !== message.id)
        .concat([
          {
            id: tempAssistantId,
            role: 'assistant',
            message: '',
            content: '',
            imageUrls: [],
            generatedImageUrls: [],
            createdAt: new Date().toISOString(),
            streaming: true
          }
        ])
    );

    const controller = new AbortController();
    abortRef.current = controller;
    const generated = [];
    try {
      await regenerateConversationMessageApi({
        messageId: message.id,
        provider: activeModel.provider || DEFAULT_LLM_PROVIDER_KEY,
        model,
        signal: controller.signal,
        ...applyStreamHandlers(tempAssistantId, generated)
      });
    } catch (err) {
      const aborted = err?.name === 'AbortError' || controller.signal.aborted;
      if (aborted) {
        settleStoppedAssistant(tempAssistantId);
      } else {
        setError(getApiErrorMessage(err, t('chatSendError')));
        setMessages((prev) => prev.filter((item) => item.id !== tempAssistantId));
      }
    } finally {
      setSending(false);
      abortRef.current = null;
      setMessages((prev) => prev.map((item) => (item.streaming ? { ...item, streaming: false } : item)));
    }
  };

  const handleSaveEdit = async (message) => {
    const text = editingText.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    setEditingId(null);

    const tempAssistantId = `temp-assistant-${Date.now()}`;
    setMessages((prev) => {
      const index = prev.findIndex((item) => item.id === message.id);
      const kept = prev.slice(0, index + 1).map((item) =>
        item.id === message.id ? { ...item, message: text, content: text } : item
      );
      return [
        ...kept,
        {
          id: tempAssistantId,
          role: 'assistant',
          message: '',
          content: '',
          imageUrls: [],
          generatedImageUrls: [],
          createdAt: new Date().toISOString(),
          streaming: true
        }
      ];
    });

    const controller = new AbortController();
    abortRef.current = controller;
    const generated = [];
    try {
      await editConversationMessageApi({
        messageId: message.id,
        message: text,
        provider: activeModel.provider || DEFAULT_LLM_PROVIDER_KEY,
        model,
        signal: controller.signal,
        ...applyStreamHandlers(tempAssistantId, generated)
      });
    } catch (err) {
      const aborted = err?.name === 'AbortError' || controller.signal.aborted;
      if (aborted) {
        settleStoppedAssistant(tempAssistantId);
      } else {
        setError(getApiErrorMessage(err, t('chatSendError')));
      }
    } finally {
      setSending(false);
      abortRef.current = null;
      setMessages((prev) => prev.map((item) => (item.streaming ? { ...item, streaming: false } : item)));
    }
  };

  const isEmpty = !loading && messages.length === 0;
  const firstUserMessage = useMemo(
    () => messages.find((item) => item.role === 'user')?.message || messages.find((item) => item.role === 'user')?.content || '',
    [messages]
  );
  const canShare = Boolean(conversationId) && !isTemporary;
  const hasContent = Boolean(prompt.trim() || attachments.length);
  const showVoiceBar = speechInput.listening;

  const toggleTemporary = () => {
    if (conversationId) return;
    const next = !isTemporary;
    setIsTemporary(next);
    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set('temporary', '1');
    } else {
      params.delete('temporary');
    }
    const search = params.toString();
    navigate({ pathname: '/chat', search: search ? `?${search}` : '' }, { replace: true });
  };

  const composer = (
    <form onSubmit={handleSend} className="mx-auto w-full max-w-3xl">
      {activeTool ? (
        <div className="mb-2 flex justify-start px-1">
          <ChatToolChip tool={activeTool} onClear={() => setActiveTool(null)} />
        </div>
      ) : null}

      {attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2 px-1">
          {attachments.map((item) => (
            item.kind === 'image' ? (
              <div
                key={item.id}
                className="relative h-14 w-14 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800"
              >
                <img src={item.previewUrl || item.url} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  disabled={sending || uploading}
                  onClick={() => handleRemoveAttachment(item)}
                  className="absolute end-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-[10px] text-white"
                >
                  ×
                </button>
              </div>
            ) : (
              <div
                key={item.id}
                className="relative inline-flex max-w-[14rem] items-center gap-2 rounded-xl border border-neutral-200 bg-white px-2.5 py-2 text-xs text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200"
              >
                <FileText size={14} className="shrink-0 opacity-70" />
                <span className="truncate">{item.name}</span>
                <button
                  type="button"
                  disabled={sending || uploading}
                  onClick={() => handleRemoveAttachment(item)}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-200 text-[10px] text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                >
                  ×
                </button>
              </div>
            )
          ))}
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_CHAT_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) handleAddFiles(files);
          event.target.value = '';
        }}
      />

      {showVoiceBar ? (
        <VoiceDictationBar
          listening={speechInput.listening}
          onStop={() => speechInput.stop()}
          onSend={() => handleSend()}
          onAdd={() => fileInputRef.current?.click()}
          canSend={Boolean(prompt.trim())}
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm dark:bg-panel-strong">
          <QuoteBar text={quote?.text} onClear={() => setQuote(null)} />
          <div className="flex items-end gap-1 px-2 py-2">
            <ChatComposerPlusMenu
              disabled={sending || uploading}
              activeTool={activeTool}
              onSelectTool={setActiveTool}
              onAttachFiles={handleAddFiles}
            />
            <textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={1}
              placeholder={t('chatAskPlaceholder')}
              className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent py-2.5 text-base text-text outline-none placeholder:text-muted md:text-sm"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (!sending) handleSend(event);
                }
              }}
            />
            <button
              type="button"
              disabled={sending}
              onClick={toggleVoiceInput}
              className={classNames(
                'mb-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full transition disabled:opacity-40',
                speechInput.listening
                  ? 'bg-red-500 text-white'
                  : 'text-neutral-500 hover:bg-neutral-200/70 hover:text-neutral-800'
              )}
              title={t('chatVoiceInput')}
            >
              <Mic size={16} />
            </button>
            {sending ? (
              <button
                type="button"
                onClick={stopGeneration}
                className="mb-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-white transition hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
                aria-label={t('stopRequest')}
                title={t('stopRequest')}
              >
                <Square size={12} fill="currentColor" strokeWidth={0} />
              </button>
            ) : hasContent ? (
              <button
                type="submit"
                disabled={uploading || freeQuotaExhausted || (!prompt.trim() && attachments.length === 0)}
                className="mb-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-white transition enabled:hover:bg-neutral-800 disabled:opacity-40 dark:bg-white dark:text-neutral-950"
                aria-label={t('send')}
              >
                <ArrowUp size={16} />
              </button>
            ) : null}
          </div>
        </div>
      )}
      <ChatFreeQuotaBanner entitlement={entitlement} />
      {error ? <p className="mt-2 text-center text-sm text-red-500">{error}</p> : null}
      {!isEmpty ? (
        <p className="mt-3 pb-2 text-center text-[11px] leading-5 text-neutral-400">{t('chatDisclaimer')}</p>
      ) : null}
    </form>
  );

  const renderMessage = (message) => {
    const isUser = message.role === 'user';
    const text = message.content || message.message || '';

    if (isUser) {
      return (
        <div key={message.id} className="flex flex-col items-start">
          <QuotedMessagePreview text={message.quotedText} className="self-start text-start" />
          <div className="max-w-[85%] rounded-3xl bg-neutral-100 px-4 py-3 text-base leading-8 text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100">
            {(message.imageUrls || []).length > 0 ? (
              <ChatMessageAttachments urls={message.imageUrls} className="mb-2" />
            ) : null}
            {editingId === message.id ? (
              <div className="space-y-2">
                <textarea
                  value={editingText}
                  onChange={(event) => setEditingText(event.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-950"
                />
                <div className="flex justify-start gap-2">
                  <button
                    type="button"
                    className="text-xs text-neutral-500"
                    onClick={() => setEditingId(null)}
                  >
                    {t('chatCancelEdit')}
                  </button>
                  <button
                    type="button"
                    className="text-xs font-medium text-neutral-900 dark:text-white"
                    onClick={() => handleSaveEdit(message)}
                  >
                    {t('chatSaveEdit')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap">{text}</div>
            )}
          </div>
          {editingId !== message.id ? (
            <UserMessageActions
              className="ms-1"
              disabled={sending}
              onCopy={() => copyTextToClipboard(text)}
              onEdit={() => {
                setEditingId(message.id);
                setEditingText(text);
              }}
            />
          ) : null}
        </div>
      );
    }

    return (
      <div key={message.id} className="flex w-full flex-col items-start" data-assistant-message={message.id}>
        <div className="w-full px-1 py-1 text-base leading-8 text-neutral-900 dark:text-neutral-100">
          {(message.imageUrls || []).length > 0 ? (
            <ChatMessageAttachments urls={message.imageUrls} className="mb-2" />
          ) : null}

          {text ? (
            <MessageMarkdown content={text} />
          ) : message.streaming ? (
            <span className="inline-flex items-center gap-1 text-neutral-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400 [animation-delay:120ms]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-400 [animation-delay:240ms]" />
            </span>
          ) : null}

          {(message.generatedImageUrls || []).length > 0 ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {message.generatedImageUrls.map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt="" className="w-full rounded-xl object-cover" />
                </a>
              ))}
            </div>
          ) : null}
        </div>
        {!message.streaming ? (
          <AssistantMessageActions
            className="ms-1"
            feedback={message.feedback}
            disabled={sending || String(message.id).startsWith('temp-')}
            onCopy={() => copyTextToClipboard(text)}
            onRegenerate={() => handleRegenerate(message)}
            onLike={() => handleFeedback(message, 'like')}
            onDislike={() => handleFeedback(message, 'dislike')}
          />
        ) : null}
      </div>
    );
  };

  return (
    <DashboardLayout
      flush
      title={conversationTitle || ''}
      startActions={
        <ModelQuickDropdown
          kind="chat"
          modelKey={model}
          returnTo={returnPath}
          onChange={setModel}
        />
      }
      actions={
        <div className="flex items-center gap-2">
          {folderId ? (
            <Link
              to={`/folders/${folderId}`}
              className="hidden max-w-[10rem] truncate rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-50 sm:inline-flex dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-900"
            >
              {folderName || t('folders')}
            </Link>
          ) : null}
          {canShare ? <ShareConversationButton onClick={() => setShareOpen(true)} /> : null}
          <TemporaryChatButton
            active={isTemporary}
            disabled={Boolean(conversationId)}
            onClick={toggleTemporary}
          />
        </div>
      }
    >
      {canShare ? (
        <ShareConversationDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          conversationId={conversationId}
          title={conversationTitle}
          firstUserMessage={firstUserMessage}
          messageCount={messages.length}
          createdAt={conversationCreatedAt}
        />
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col">
        {isEmpty ? (
          <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col items-center justify-center px-4 sm:px-6">
            {isTemporary ? (
              <div className="mb-8 max-w-xl text-center">
                <h1 className="text-3xl font-bold tracking-tight text-neutral-950 dark:text-white sm:text-4xl">
                  {t('chatTemporary')}
                </h1>
                <p className="mt-3 text-sm leading-7 text-neutral-500 dark:text-neutral-400">
                  {t('chatTemporaryDescription')}
                </p>
              </div>
            ) : (
              <div className="mb-10 text-center">
                {folderId ? (
                  <Link
                    to={`/folders/${folderId}`}
                    className="mb-3 inline-flex rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300"
                  >
                    {folderName || t('folders')}
                  </Link>
                ) : null}
                <h1 className="text-3xl font-bold tracking-tight text-neutral-950 dark:text-white sm:text-4xl">
                  {t('chatGreeting')}
                </h1>
              </div>
            )}
            <div className="w-full">{composer}</div>
          </div>
        ) : (
          <>
            <div ref={messagesContainerRef} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-4xl space-y-5 px-4 pb-4 pt-6 sm:px-6 sm:pt-8">
                {loading ? (
                  <div className="flex h-40 items-center justify-center text-neutral-400">
                    <LoaderCircle className="animate-spin" size={22} />
                  </div>
                ) : null}
                {messages.map(renderMessage)}
                <div ref={bottomRef} />
              </div>
            </div>
            <AskRismanSelection
              containerRef={messagesContainerRef}
              enabled={!sending}
              onAsk={(text) => setQuote({ text, messageId: null })}
            />
            <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">{composer}</div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};
